import { Component, EventEmitter, Input, OnInit, Output, DestroyRef, inject} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { AuthService } from '@app/core/auth/services/auth.service'
import { allPermissions } from '@app/core/auth/permissions'
import { ApiDataResponse } from '@app/models/others.model'
import { RequirementDataInput, RequirementSubmission, UpdateRequirementSubmissionInput } from '@app/models/requirement_submission'
import { Role } from '@app/models/role.model'
import { ListConfig } from '@app/models/shared.model'
import { RequirementType, RequirementStatus } from '@app/shared/app.enums'
import { DynamicFormControlValidatorService } from '@app/shared/services/dynamic-form-control-validator.service'
import { TranslateService } from '@ngx-translate/core'

import { RequirementSubmissionsService } from '../services/requirement-submissions.service'
import { CommonService } from '@app/shared/services/common.service'
import { MessageService } from 'primeng/api'

@Component({
  selector: 'app-modal-requirement-submission-form',
  templateUrl: './modal-requirement-submission-form.component.html',
  styleUrls: ['./modal-requirement-submission-form.component.scss'],
  providers: [DynamicFormControlValidatorService, MessageService],
  standalone: false,
})
export class ModalRequirementSubmissionFormComponent implements OnInit {
  private destroyRef = inject(DestroyRef)
  visible: boolean = false
  private isSaving: boolean = false
  form: FormGroup
  position = document.dir === 'rtl' ? 'left' : 'right'

  // Image preview properties
  imagePreviewVisible: boolean = false
  previewImageUrl: string = ''
  imageRotation: number = 0
  imageZoom: number = 1

  @Input() model: any = {}
  @Output() modelChange = new EventEmitter<RequirementSubmission>()
  @Output() onChange = new EventEmitter<RequirementSubmission>()

  viewMode: boolean = false
  entryPurpose: string = ''

  currentRequirement: RequirementDataInput & { isNewestLog?: boolean } | null = null

  submittedRequirements: RequirementDataInput[]
  roles: Role[] = []
  defaultFormState: any = {}
  categoryId: number
  branchesListConfig: ListConfig
  requirementTypes = Object.values(RequirementType)
  minReviewedExpirationDate = new Date()

  constructor(
    private fb: FormBuilder,
    private requirementSubmissionService: RequirementSubmissionsService,
    private formValidatorService: DynamicFormControlValidatorService,
    private translate: TranslateService,
    private authService: AuthService,
    private commonService: CommonService,
    private messageService: MessageService,
  ) {
    this.branchesListConfig = new ListConfig({
      data: [],
      optionValue: 'id',
      optionLabel: 'name',
      filter: false,
      showClear: true,
      multiSelectionDisplay: 'chip',
      emptyMessage: this.translate.instant('COMMON.MESSAGES.NO_RESULTS'),
    })
  }

  ngOnInit(): void {
    this.formBuild()
  }


  isRequired(controlName: string, index: number): boolean {
    const control = this.getSubmissionFormGroup(index)?.get(controlName) as FormControl
    if (!control) {
      return false
    }
    return !!(control.validator && !control.valid && !control.disabled)
  }

  get typeControl(): FormControl {
    return this.form.get('type') as FormControl
  }

  formBuild() {
    this.form = this.fb.group({
      submissions: this.fb.array([]),
      viewModeForm: this.fb.group({
        reviewedExpirationDate: [null],
      }),
    })

    this.defaultFormState = this.form.value
  }

  get submissions(): FormArray {
    return this.form.get('submissions') as FormArray
  }

  get viewModeForm(): FormGroup {
    return this.form.get('viewModeForm') as FormGroup
  }

  get expirationDateControl(): FormControl {
    return this.viewModeForm.get('expirationDate') as FormControl
  }

  get reviewedExpirationDateControl(): FormControl {
    return this.viewModeForm.get('reviewedExpirationDate') as FormControl
  }

  private customFieldsCache = new Map<string, any[]>()

  /**
   * Parse custom field data from submitted_data string array
   */
  parseCustomFieldData(submittedData: string[] | string | null, customFieldsSchema?: string | any[] | null): any[] {
    const cacheKey = JSON.stringify({ submittedData, customFieldsSchema })

    if (this.customFieldsCache.has(cacheKey)) {
      return this.customFieldsCache.get(cacheKey)!
    }

    if (!submittedData || !Array.isArray(submittedData)) {
      const result: any[] = []
      this.customFieldsCache.set(cacheKey, result)
      return result
    }

    // Handle custom fields schema - could be a string (old) or already parsed array (new after package upgrades)
    let schema: any[] = []
    if (customFieldsSchema) {
      if (Array.isArray(customFieldsSchema)) {
        schema = customFieldsSchema
      } else if (typeof customFieldsSchema === 'string') {
        try {
          schema = JSON.parse(customFieldsSchema)
        } catch (e) {
          console.warn('Failed to parse custom_fields_schema:', e)
        }
      }
    }

    const customFields: any[] = []

    submittedData.forEach((item) => {
      try {
        // New format: "field_id: value". The id is matched against the schema rather than
        // against a UUID shape — C-Portal generates UUIDs, but PAPI accepts any string id, so
        // anything created outside this form (API, System Fields seeded elsewhere) would
        // otherwise be dropped from the view. A URL or base64 blob has no schema entry and is
        // still skipped.
        if (!item.startsWith('{') && item.includes(':')) {
          const [fieldId, ...valueParts] = item.split(':')
          const value = valueParts.join(':').trim()
          const trimmedFieldId = fieldId.trim()

          const fieldDef = schema.find((f) => f.id === trimmedFieldId)

          if (fieldDef) {
            customFields.push({
              id: trimmedFieldId,
              label: fieldDef.label,
              type: fieldDef.type,
              data: value,
              column_name: fieldDef.column_name,
            })
          }
        }
        // Old format: JSON strings (backwards compatibility)
        else if (
          item.startsWith('{') &&
          (item.includes('"id"') || item.includes("'id'")) &&
          (item.includes('"type"') || item.includes("'type'")) &&
          (item.includes('"label"') || item.includes("'label'"))
        ) {
          let jsonString = item
          if (item.includes("'id'")) {
            jsonString = item.replace(/'/g, '"')
          }

          const fieldData = JSON.parse(jsonString)
          customFields.push(fieldData)
        }
      } catch (e) {
        console.warn('Failed to parse custom field data:', item, e)
      }
    })

    this.customFieldsCache.set(cacheKey, customFields)
    return customFields
  }

  /**
   * YB-2124 — is this a CHANGE to an answer that was already approved?
   *
   * The undo buffer PAPI keeps while a self-edit awaits review is what says so. Its presence means
   * the approver is being asked about a difference, not about a fresh submission — and that
   * rejecting it restores the previous value rather than leaving the person refused at the gate.
   */
  isSelfEdit(requirement: any): boolean {
    return !!requirement && (!!requirement.previous_form_data || !!requirement.previous_submitted_data?.length)
  }

  /**
   * What this field held before the holder changed it, or null when it did not move.
   *
   * Reuses parseCustomFieldData so the previous values are read exactly the way the current ones
   * are — one parser, so the two sides of the comparison can never disagree about format.
   */
  previousValueOf(requirement: any, fieldId: string): string | null {
    if (!this.isSelfEdit(requirement)) return null

    const previousField = this.parseCustomFieldData(requirement.previous_submitted_data ?? null, requirement.custom_fields_schema).find((field) => field.id === fieldId)
    if (!previousField) return null

    const currentField = this.parseCustomFieldData(requirement.submitted_data, requirement.custom_fields_schema).find((field) => field.id === fieldId)
    const previousValue = this.formatCustomFieldValue(previousField)

    // Only the fields that actually moved: a form of eight fields where one plate changed should
    // show one strike-through, not eight.
    return currentField && this.formatCustomFieldValue(currentField) === previousValue ? null : previousValue
  }

  private imageUrlsCache = new Map<string, string[]>()

  /**
   * Get image URLs from submitted_data
   */
  getImageUrls(requirement: any): string[] {
    const submittedData = requirement.submitted_data

    if (!submittedData || !Array.isArray(submittedData)) {
      return []
    }

    // Filter items that start with http:// or https:// - these are image URLs
    const urls = submittedData.filter((item) => item.startsWith('http://') || item.startsWith('https://'))

    return urls
  }

  /**
   * Format custom field value based on its type
   */
  formatCustomFieldValue(field: any): string {
    if (!field || !field.data) return ''

    switch (field.type) {
      case 'date':
        try {
          const date = new Date(field.data)
          return date.toLocaleDateString()
        } catch (_e) {
          return field.data
        }
      case 'number':
        return field.data.toString()
      case 'text':
      default:
        return field.data
    }
  }

  /**
   * Copy custom field value to clipboard
   */
  copyCustomFieldValue(field: any): void {
    if (!field || !field.data) return

    const value = this.formatCustomFieldValue(field)
    this.commonService.copyTextToClipboard(value).then((success) => {
      if (success) {
        this.messageService.add({
          severity: 'success',
          icon: 'pi pi-check-circle color-success',
          summary: this.translate.instant('EVENTS.LABELS.VALUE_SUCCESSFULLY_COPIED'),
        })
      } else {
        this.messageService.add({
          severity: 'error',
          icon: 'pi pi-times-circle',
          summary: this.translate.instant('EVENTS.LABELS.VALUE_COPY_FAILED'),
        })
      }
    })
  }

  /**
   * Debug method to help troubleshoot custom field parsing
   */
  debugCustomFields(_submittedData: string[] | string | null): void {
    // Debug method - can be removed in production
  }

  /**
   * Open image in new tab for preview
   */
  openImagePreview(imageUrl: string): void {
    window.open(imageUrl, '_blank')
  }

  /**
   * Open custom image preview modal
   */
  openCustomImagePreview(imageUrl: string): void {
    this.previewImageUrl = imageUrl
    this.imageRotation = 0 // Reset rotation when opening new image
    this.imageZoom = 1 // Reset zoom when opening new image
    this.imagePreviewVisible = true
  }

  /**
   * Close image preview modal
   */
  closeImagePreview(): void {
    this.imagePreviewVisible = false
    this.previewImageUrl = ''
    this.imageRotation = 0
    this.imageZoom = 1
  }

  /**
   * Rotate image 90 degrees clockwise
   */
  rotateClockwise(): void {
    this.imageRotation += 90
  }

  /**
   * Rotate image 90 degrees counter-clockwise
   */
  rotateCounterClockwise(): void {
    this.imageRotation -= 90
  }

  /**
   * Reset image rotation to 0
   */
  resetImageRotation(): void {
    this.imageRotation = 0
  }

  /**
   * Zoom in on the image
   */
  zoomIn(): void {
    this.imageZoom = Math.min(this.imageZoom * 1.2, 5) // Max zoom 5x
  }

  /**
   * Zoom out on the image
   */
  zoomOut(): void {
    this.imageZoom = Math.max(this.imageZoom / 1.2, 0.5) // Min zoom 0.5x
  }

  /**
   * Reset image zoom
   */
  resetZoom(): void {
    this.imageZoom = 1
  }

  /**
   * Check if the current user can approve a specific requirement
   */
  canApproveRequirement(requirementId: number): boolean {
    const currentUser = this.authService.user

    // First check if user has the approve/reject permission
    if (!this.hasApproveRejectPermission()) {
      return false
    }

    // If user has no assigned requirements, they can approve all (legacy behavior)
    if (!currentUser?.approver_requirement_ids || currentUser.approver_requirement_ids.length === 0) {
      return true
    }

    // Check if this requirement is in the user's assigned list
    return currentUser.approver_requirement_ids.includes(requirementId)
  }

  /**
   * Check if the current user has the approve/reject permission
   */
  hasApproveRejectPermission(): boolean {
    const currentUser = this.authService.user
    if (!currentUser || !currentUser.role || !currentUser.role.permissions) {
      return false
    }
    return currentUser.role.permissions.includes(allPermissions.requirement_submissions.groups.general.components.approve_reject_submission)
  }

  /**
   * Get approval progress text for a requirement (e.g., "2 of 3 approvals")
   */
  getApprovalProgress(requirement: RequirementDataInput): string {
    const currentApprovals = requirement.approvals?.length || 0
    const requiredApprovals = (requirement as any).required_approvals || 1
    return `${currentApprovals} / ${requiredApprovals}`
  }

  /**
   * Check if the current user has already approved this requirement
   */
  hasUserApproved(requirement: RequirementDataInput): boolean {
    const currentUserId = this.authService.user?.id
    if (!currentUserId || !requirement.approvals) {
      return false
    }
    return requirement.approvals.some((approval) => Number(approval.user_id) === Number(currentUserId))
  }

  /**
   * Check if user can approve (has permission, assigned to requirement, and hasn't approved yet)
   */
  canUserApprove(requirement: RequirementDataInput): boolean {
    return this.canApproveRequirement(requirement.id) && !this.hasUserApproved(requirement)
  }

  private createRequirementGroup(requirement: RequirementDataInput): FormGroup {
    // Convert reviewed_expiration_date to local date without timezone offset
    let reviewedExpirationDate = null
    if (requirement.reviewed_expiration_date) {
      const utcDate = new Date(requirement.reviewed_expiration_date)
      // Create a new date using the UTC date components to avoid timezone shifts
      reviewedExpirationDate = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate())
    }

    const group = this.fb.group({
      status: [requirement.status === RequirementStatus.IN_REVIEW ? null : (requirement.status as RequirementStatus | null), Validators.required],
      rejection_reason: [requirement.rejection_reason || ''],
      reviewed_expiration_date: [reviewedExpirationDate],
      conditional_answer: [null],
    })

    group.get('status')!.valueChanges.subscribe((status: RequirementStatus | null) => {
      const rejectionReasonControl = group.get('rejection_reason')!
      const reviewedExpirationDateControl = group.get('reviewed_expiration_date')!

      if (status === RequirementStatus.REJECTED) {
        rejectionReasonControl.setValidators([Validators.required])
        reviewedExpirationDateControl.clearValidators()
      } else if ((status === RequirementStatus.IN_REVIEW || status === RequirementStatus.APPROVED) && requirement.requires_expiration) {
        rejectionReasonControl.clearValidators()
        reviewedExpirationDateControl.setValidators([Validators.required])
      } else {
        rejectionReasonControl.clearValidators()
        reviewedExpirationDateControl.clearValidators()
      }

      rejectionReasonControl.updateValueAndValidity()
      reviewedExpirationDateControl.updateValueAndValidity()
    })

    return group
  }

  getDateFromString(dateString: string | string[] | null): Date | null {
    if (!dateString) {
      return null
    }

    // If it's an array, use the first element if available
    if (Array.isArray(dateString)) {
      return dateString.length > 0 ? new Date(dateString[0]) : null
    }

    // Otherwise use the string directly
    return new Date(dateString)
  }

  getStatusControl(index: number): FormControl {
    return this.getSubmissionFormGroup(index).get('status') as FormControl
  }

  getRejectionReasonControl(index: number): FormControl {
    return this.getSubmissionFormGroup(index).get('rejection_reason') as FormControl
  }

  getSubmissionFormGroup(index: number): FormGroup {
    return this.submissions.at(index) as FormGroup
  }

  getReviewedExpirationDateControl(index: number): FormControl {
    return this.getSubmissionFormGroup(index).get('reviewed_expiration_date') as FormControl
  }

  getConditionalAnswerControl(index: number): FormControl {
    return this.getSubmissionFormGroup(index).get('conditional_answer') as FormControl
  }

  open(model: RequirementSubmission, requirementLogOrKey?: any, viewMode: boolean = false) {
    // Clear caches when opening new data
    this.customFieldsCache.clear()
    this.imageUrlsCache.clear()

    this.model = model
    this.viewMode = viewMode
    this.entryPurpose = ''

    // Parse entry_purpose from type_specific_data if available (for vehicles/HME)
    if (model.type_specific_data && (model.pass_category === 17 || model.pass_category === 18)) {
      try {
        const typeSpecificData = JSON.parse(model.type_specific_data)
        this.entryPurpose = typeSpecificData.entry_purpose || ''
      } catch (error) {
        console.warn('Failed to parse type_specific_data:', error)
      }
    }

    // Convert each 'data' parameter from a comma-separated string into an array
    // For view-only users, show all requirements but disable approval
    // For approver users, filter to only show requirements they can approve
    this.submittedRequirements = model.requirements
      .filter((req) => {
        // If user has approve/reject permission, filter by assigned requirements
        if (this.hasApproveRejectPermission()) {
          return this.canApproveRequirement(req.id)
        }
        // If user only has view permission, show all requirements
        return true
      })
      .map((req) => {
        if (!Array.isArray(req.submitted_data)) {
          req.submitted_data = []
        }
        return req
      })

    this.visible = true
    this.form.enable()

    if (viewMode && requirementLogOrKey) {
      // Check if we received a FlattenedRequirement object directly
      if (typeof requirementLogOrKey === 'object' && requirementLogOrKey.id) {
        // Store whether this is the newest log to control editability
        const isNewestLog = requirementLogOrKey.isNewestLog !== undefined ? requirementLogOrKey.isNewestLog : true

        // Use the requirementLog data directly
        const requirementId = requirementLogOrKey.id
        const submissionId = requirementLogOrKey.submission_id

        // Find the matching requirement in the submission
        this.currentRequirement = this.submittedRequirements.find((req) => String(req.id) === String(requirementId) && String(req.submission_id) === String(submissionId)) ?? null

        if (this.currentRequirement) {
          // Store the isNewestLog flag in the currentRequirement
          this.currentRequirement = {
            ...this.currentRequirement,
            isNewestLog,
          }

          // Override the requirement's expiration date with the value from the log
          if (requirementLogOrKey.reviewed_expiration_date) {
            // Use the date from the requirement log directly
            this.reviewedExpirationDateControl.setValue(requirementLogOrKey.reviewed_expiration_date)

            // Disable the control if it's not the newest log
            if (!isNewestLog) {
              this.reviewedExpirationDateControl.disable()
            } else {
              this.reviewedExpirationDateControl.enable()
            }
          }

          // Only set validators if it requires expiration and is the newest log
          if (this.currentRequirement.requires_expiration && isNewestLog) {
            this.reviewedExpirationDateControl.setValidators([Validators.required])
          } else {
            this.reviewedExpirationDateControl.clearValidators()
          }
          this.reviewedExpirationDateControl.updateValueAndValidity()
        }
      } else {
        // Handle legacy cases...
      }
    } else {
      // Regular edit mode for active submissions
      this.formValidatorService.switchFormValidator()

      // Reset the form array and repopulate it
      const submissionsArray = this.form.get('submissions') as FormArray
      submissionsArray.clear()
      this.submittedRequirements.forEach((req) => {
        submissionsArray.push(this.createRequirementGroup(req))
      })
    }
  }

  cancel() {
    // Clear caches when closing
    this.customFieldsCache.clear()
    this.imageUrlsCache.clear()

    this.viewMode = false
    this.currentRequirement = null
    this.form.reset(this.defaultFormState)
    this.visible = false
  }

  save() {
    if (this.viewMode && this.currentRequirement) {
      this.saveExpirationDateOnly()
    } else {
      this.saveFullForm()
    }
  }

  saveExpirationDateOnly() {
    if (!this.currentRequirement || !this.currentRequirement?.id) {
      this.cancel()
      return
    }

    this.isSaving = true
    this.form.disable()

    // Get the new expiration date
    const newReviewedExpirationDate = this.reviewedExpirationDateControl.value

    // Prepare a full update payload with all requirements, but only modify the current one
    const allRequirements = this.submittedRequirements.map((req) => {
      const requirementData: any = {
        id: req.id,
        type: req.type,
        submitted_data: req.submitted_data,
        status: req.status,
      }

      // For the current requirement being edited, update the expiration date
      if (req.id === this.currentRequirement?.id) {
        if (newReviewedExpirationDate) {
          // Create a UTC date that represents 23:59:59.999 of the selected day
          const localDate = new Date(newReviewedExpirationDate)
          const expirationDate = new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 23, 59, 59, 999))
          requirementData.reviewed_expiration_date = expirationDate
        } else {
          // Explicitly set to null if cleared
          requirementData.reviewed_expiration_date = null
        }
      }

      // Include other necessary fields if they exist
      if (req.rejection_reason) requirementData.rejection_reason = req.rejection_reason
      if (req.requires_expiration) requirementData.requires_expiration = req.requires_expiration
      if (req.expiration_date) requirementData.expiration_date = req.expiration_date
      if (req.approved_at) requirementData.approved_at = req.approved_at
      if (req.reviewed_expiration_date && req.id !== this.currentRequirement?.id) {
        requirementData.reviewed_expiration_date = req.reviewed_expiration_date
      }

      return requirementData
    })

    const updatePayload: UpdateRequirementSubmissionInput = {
      pass_id: this.model.pass_id,
      user_language: this.model.user_language,
      requirements: allRequirements,
    }

    this.requirementSubmissionService
      .update(updatePayload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: ApiDataResponse<RequirementSubmission[]>) => {
        this.form.enable()
        this.isSaving = false
        if (data.success) {
          this.model = { ...this.model, ...data.data[0] }
          this.modelChange.emit(this.model)
          this.onChange.emit(data.data[0] as RequirementSubmission)
          this.cancel()
        }
      })
  }

  saveFullForm() {
    this.isSaving = true
    this.form.disable()

    const formData = this.form.getRawValue()

    // Build an array of updates with only the necessary fields
    const updatedSubmissions = formData.submissions.map((requirement: any, index: number) => {
      const submission = this.submittedRequirements[index]

      const update: any = {
        id: submission.id,
        type: submission.type,
        submitted_data: submission.submitted_data,
        status: requirement.status,
      }

      if (requirement.status === RequirementStatus.REJECTED) {
        update.rejection_reason = requirement.rejection_reason
      }

      // Handle conditional question answer
      if (submission.conditional_check && requirement.conditional_answer) {
        if (requirement.conditional_answer === 'no') {
          // User said no, set the declined timestamp
          update.conditional_declined_at = new Date()
        } else {
          // User said yes, clear any previous declined timestamp
          update.conditional_declined_at = null
        }
      }

      // Handle reviewed_expiration_date based on approval status
      // When user approves (IN_REVIEW) or status is already APPROVED, we set the expiration date if required
      if ((requirement.status === RequirementStatus.IN_REVIEW || requirement.status === RequirementStatus.APPROVED) && submission.requires_expiration) {
        if (requirement.reviewed_expiration_date) {
          // Get the date components from the local date
          const localDate = new Date(requirement.reviewed_expiration_date)

          // Create a UTC date that represents 23:59:59.999 of the selected day
          // This ensures it will be the end of the day in UTC time regardless of local timezone
          const expirationDate = new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate(), 23, 59, 59, 999))

          update.reviewed_expiration_date = expirationDate
        } else {
          // Explicitly set to null if cleared
          update.reviewed_expiration_date = null
        }
      } else {
        // If not approving or doesn't require expiration, set to null
        update.reviewed_expiration_date = null
      }

      return update
    })

    // Only proceed if there are requirements to update
    if (updatedSubmissions.length === 0) {
      this.form.enable()
      this.isSaving = false
      // Could show a message here if needed
      return
    }

    // Prepare the data to send to the server
    const updatePayload: UpdateRequirementSubmissionInput = {
      pass_id: this.model.pass_id,
      user_language: this.model.user_language,
      requirements: updatedSubmissions,
    }

    this.requirementSubmissionService
      .update(updatePayload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: ApiDataResponse<RequirementSubmission[]>) => {
        this.form.enable()
        this.isSaving = false
        if (data.success) {
          this.model = { ...this.model, ...data.data[0] }
          this.modelChange.emit(this.model)
          this.onChange.emit(data.data[0] as RequirementSubmission)
          this.cancel()
        }
      })
  }

  _onHide() {
    if (this.visible) {
      this.cancel()
    }
  }
}
